import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CartItem {
  id: number;
  name: string;
  image: string;
  price: number;
  quantity: number;
}

interface CartStore {
  items: CartItem[];
  totalQuantity: number;
  totalAmount: number;

  // Actions
  addToCart: (course: { id: number; name: string; image: string; price: number }) => void;
  removeFromCart: (id: number) => void;
  clearCart: () => void;
  updateQuantity: (id: number, quantity: number) => void;

  // Computed
  getItemQuantity: (id: number) => number;
  isInCart: (id: number) => boolean;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      totalQuantity: 0,
      totalAmount: 0,

      addToCart: (course) => {
        const { items } = get();
        const existingItem = items.find((item) => item.id === course.id);

        if (existingItem) {
          // Item already exists, don't add again
          return;
        }

        const newItem: CartItem = {
          ...course,
          quantity: 1,
        };

        const newItems = [...items, newItem];
        const totalQuantity = newItems.reduce((sum, item) => sum + item.quantity, 0);
        const totalAmount = newItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

        set({
          items: newItems,
          totalQuantity,
          totalAmount,
        });
      },

      removeFromCart: (id) => {
        const { items } = get();
        const newItems = items.filter((item) => item.id !== id);
        const totalQuantity = newItems.reduce((sum, item) => sum + item.quantity, 0);
        const totalAmount = newItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

        set({
          items: newItems,
          totalQuantity,
          totalAmount,
        });
      },

      clearCart: () => {
        set({
          items: [],
          totalQuantity: 0,
          totalAmount: 0,
        });
      },

      updateQuantity: (id, quantity) => {
        const { items } = get();
        const newItems = items.map((item) =>
          item.id === id ? { ...item, quantity: Math.max(1, quantity) } : item
        );
        const totalQuantity = newItems.reduce((sum, item) => sum + item.quantity, 0);
        const totalAmount = newItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

        set({
          items: newItems,
          totalQuantity,
          totalAmount,
        });
      },

      getItemQuantity: (id) => {
        const { items } = get();
        const item = items.find((item) => item.id === id);
        return item ? item.quantity : 0;
      },

      isInCart: (id) => {
        const { items } = get();
        return items.some((item) => item.id === id);
      },
    }),
    {
      name: 'cart-storage',
      partialize: (state) => ({
        items: state.items,
        totalQuantity: state.totalQuantity,
        totalAmount: state.totalAmount,
      }),
    }
  )
);
